import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { Seo } from "../lib/seo";
import { SITE, telLink } from "../lib/site";
import { getService, SERVICES } from "../data/services";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { EnquiryForm } from "../components/EnquiryForm";

export const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);
  const imgRef = useRef(null);
  const inView = useInView(imgRef, { once: true, amount: 0.3 });

  if (!service) return <Navigate to="/404" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={service.title}
        description={service.summary}
        path={`/services/${service.slug}`}
      />
      <PageHero
        eyebrow={`Legal Services · ${service.num}`}
        title={[service.title.split(" & ")[0] + " &", service.title.split(" & ").slice(1).join(" & ")]}
        description={service.intro}
      >
        <Reveal delay={0.5} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Link to="/contact" data-testid="service-enquiry-cta-button" className="btn-gold">
            Enquire about this service <ArrowRight size={16} />
          </Link>
          <a href={telLink} className="btn-outline-light">
            <Phone size={15} /> {SITE.phoneDisplay}
          </a>
        </Reveal>
      </PageHero>

      <section className="bg-white py-24 sm:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">What we assist with</p>
              <h2 className="mt-5 max-w-xl text-3xl font-medium tracking-tight text-ink sm:text-4xl">
                {service.title.split(" & ").join(" & ")} — scope of work.
              </h2>
            </Reveal>
            <Stagger className="mt-12 space-y-0 border-t border-line">
              {service.points.map((p, i) => (
                <StaggerItem key={p.t}>
                  <div className="group grid gap-2 border-b border-line py-7 sm:grid-cols-[3rem_1fr]">
                    <span className="font-mono text-xs text-gold-dark">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-xl text-ink sm:text-2xl">{p.t}</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-steel">{p.d}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-gold/40 bg-cream p-9 lg:sticky lg:top-28">
                <p className="eyebrow">How we engage</p>
                <p className="mt-4 text-sm leading-relaxed text-steel">
                  Legal work spans different kinds of help. For this service,
                  here is exactly what each involves:
                </p>
                <ul className="mt-7 space-y-5">
                  {service.engagement.map((e) => (
                    <li key={e.label} className="border-t border-gold/25 pt-4 first:border-0 first:pt-0">
                      <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                        <span className="h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                        {e.label}
                      </p>
                      <p className="mt-1.5 pl-4 text-sm leading-relaxed text-steel">{e.d}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="bg-canvas py-20 sm:py-24">
        <div className="container-x">
          <Reveal>
            <div ref={imgRef} className="relative">
              <div className="absolute -right-5 -top-5 h-full w-full border border-gold/50" aria-hidden="true" />
              <motion.img
                src={service.image}
                alt={service.title}
                initial={{ clipPath: "inset(0 0 0 100%)" }}
                animate={inView ? { clipPath: "inset(0 0 0 0%)" } : {}}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[21/9] w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="mt-16">
            <Reveal>
              <p className="eyebrow">Related services</p>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-3">
              {others.map((o, i) => (
                <Reveal key={o.slug} delay={i * 0.07} className="h-full">
                  <Link
                    to={`/services/${o.slug}`}
                    data-testid={`related-service-${o.slug}`}
                    className="group flex h-full flex-col justify-between card-line p-7 transition-all duration-500 hover:border-gold/60"
                  >
                    <div>
                      <span className="font-mono text-[11px] text-gold-dark">{o.num}</span>
                      <h3 className="mt-3 font-serif text-xl text-ink transition-colors group-hover:text-gold-dark">
                        {o.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-steel">{o.summary}</p>
                    </div>
                    <ArrowUpRight
                      size={17}
                      className="mt-6 self-end text-steel transition-all duration-300 group-hover:-translate-y-0.5 group-hover:text-gold-dark"
                    />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pb-24 sm:pb-28">
        <div className="container-x">
          <Reveal>
            <div className="border border-line bg-canvas p-9 sm:p-12">
              <p className="eyebrow">Quick enquiry</p>
              <h2 className="mt-4 max-w-xl font-serif text-3xl text-ink">
                Tell us briefly what you need. No documents, please.
              </h2>
              <div className="mt-10 max-w-3xl">
                <EnquiryForm compact />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
